import React from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUser, ROLES } from "../context/UserContext";

const OWNER_BENEFITS = [
  {
    key: "incomeForecast",
    icon: "📊",
    image: "/income-forecast.png",
    title: "Clear forecast of your income",
    sub: "See confirmed and upcoming bookings in one view so you always know what each car is expected to earn, instead of guessing week to week.",
  },
  {
    key: "utilisation",
    icon: "📈",
    image: "/Visibility.png",
      title: "Base, destination & live visibility",
    sub: "Multi-location visibility creates more chances for maximum bookings and higher earnings.",
  },
  {
    key: "singleConsole",
    icon: "🖥️",
    image: "/single-console.png",
    title: "Single console for all operations",
    sub: "Move away from scattered calls, chats and notebooks. Manage vehicles, bookings, drivers and earnings from one clean, consistent workspace.",
  },
  {
    key: "tripControl",
    icon: "🎯",
    image: "/trip-control.png",
    title: "You decide which trips to accept",
    sub: "Every request comes with full context — route, dates, timing and price — so you stay in control and only confirm trips that work for your business.",
  },
  {
    key: "driverOps",
    icon: "👨‍✈️",
    image: "/driver-management.png",
    title: "Structured driver & self-drive management",
    sub: "Maintain a clear driver pool, support self-drive when needed and assign or change drivers against bookings without last-minute chaos.",
  },
  {
    key: "scaleFleet",
    icon: "🚀",
    image: "/ReturnFuel.png",
      title: "Return fuel covered",
  sub: "Return fuel is covered in every booking, so you do not lose money on the way back even without a return booking.",
  },
];

function SectionShell({ title, eyebrow, children, right }) {
  return (
    <section className="mt-10">
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <div>
          {eyebrow && (
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
              {eyebrow}
            </div>
          )}
          <h2 className="mt-1 text-xl md:text-2xl font-semibold text-slate-900">
            {title}
          </h2>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function StepCard({ step, title, text }) {
  return (
    <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 px-4 py-4 shadow-sm">
      <div className="inline-flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">
          {step}
        </div>
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
          Step {step}
        </span>
      </div>
      <h3 className="mt-3 text-sm font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-xs leading-relaxed text-slate-600">{text}</p>
    </div>
  );
}

function QuickActionsRow() {
  const navigate = useNavigate();
  const { user } = useUser();

  const isSignedIn = !!user?.email;
  const isOwner = user?.role === ROLES.OWNER;

  const items = [
    { key: "cars", label: "Manage vehicles", path: "/owner/cars" },
    { key: "bookings", label: "Manage bookings", path: "/owner/bookings" },
    { key: "drivers", label: "Manage drivers", path: "/owner/drivers" },
    { key: "earnings", label: "Earnings", path: "/owner/earnings" },
  ];

  const handleClick = (path) => {
    if (isOwner && isSignedIn) {
      navigate(path);
    } else {
      navigate(
        `/vehicle-owner-signin?redirect=${encodeURIComponent(path)}`,
        { replace: false }
      );
    }
  };

  return (
    <section className="mt-4">
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
          Quick actions
        </h2>
        <span className="hidden text-[11px] text-slate-500 md:inline">
          Clear, simple shortcuts to your daily work.
        </span>
      </div>

      <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => handleClick(item.path)}
            className="group flex h-14 w-full items-center justify-center rounded-2xl border border-slate-300 bg-white text-sm font-semibold text-slate-900 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
          >
            <span className="inline-flex items-center gap-1">
              <span>{item.label}</span>
              <span className="text-xs transition-transform group-hover:translate-x-0.5">
                ›
              </span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function OwnerLanding() {
  const navigate = useNavigate();
  const { user } = useUser();

  const isSignedIn = !!user?.email;
  const isOwner = user?.role === ROLES.OWNER;

  const displayName =
    user?.userName || user?.name || user?.firstName || "there";

  const manageCarsPath = "/owner/cars";

  const handlePrimaryClick = () => {
    if (isOwner && isSignedIn) {
      navigate(manageCarsPath);
    } else {
      navigate(
        `/vehicle-owner-signin?redirect=${encodeURIComponent(manageCarsPath)}`
      );
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      <main className="bg-white">
        <div className="border-b border-slate-100 bg-gradient-to-br from-[#fff5fa] via-white to-white">
          <div className="mx-auto flex max-w-[1560px] flex-col gap-6 px-4 py-8 sm:px-6 md:flex-row md:items-center md:justify-between md:py-12 xl:px-10 2xl:px-12">
            <div className="max-w-2xl">
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl md:text-4xl">
                Run your vehicles like a business on{" "}
                <span className="text-[color:var(--brand-pink,#FF2D83)]">
                  Wayroot.
                </span>
              </h1>

              <p className="mt-4 text-sm md:text-base leading-relaxed text-slate-600">
                List your vehicles once and keep fleet, bookings, drivers and
                earnings organised in one place. Clear, calm view of your vehicle
                operations — whether you&apos;re just starting or already a
                partner.
              </p>
            </div>

            <div className="flex w-full justify-start md:w-auto md:justify-end">
              <button
                type="button"
                onClick={handlePrimaryClick}
                className="inline-flex w-full items-center justify-center rounded-2xl bg-black px-6 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(0,0,0,0.35)] transition-transform transition-shadow hover:translate-y-[1px] hover:shadow-[0_12px_30px_rgba(0,0,0,0.35)] active:translate-y-[2px] active:shadow-[0_6px_16px_rgba(0,0,0,0.40)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black sm:w-auto sm:px-10 md:text-base"
              >
                List your vehicle on Wayroot
              </button>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[1560px] px-4 pt-4 pb-8 sm:px-6 xl:px-10 2xl:px-12 md:pt-6 md:pb-10">
          <QuickActionsRow />

          <SectionShell
            eyebrow="In simple terms"
            title="How you operate and earn through Wayroot"
          >
            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <StepCard
                step={1}
                title="Set up your fleet & availability"
                text="List each car once, choose the states it can run in and manage a clean availability calendar. By default every date is bookable — you simply freeze days when the car is blocked or unavailable."
              />
              <StepCard
                step={2}
                title="Customers discover & request bookings"
                text="Your listed cars appear to customers in the Wayroot search. New trip requests land in your My bookings area where you can review route, dates and price and then accept or decline."
              />
              <StepCard
                step={3}
                title="Assign drivers or keep self-drive"
                text="Maintain a pool of trusted drivers and optionally keep self-drive as a choice. While accepting a booking you assign the driver; you can still swap drivers later up until the ride is scheduled to start."
              />
              <StepCard
                step={4}
                title="Track earnings & booking calendar"
                text="Use a clear earnings dashboard to see payouts per car, per period and per route. A consolidated calendar view shows every upcoming and completed booking across your fleet."
              />
            </div>
          </SectionShell>

          <SectionShell
            eyebrow="Why run your cars on Wayroot"
            title="What vehicle owners get from the platform"
          >
            <div className="mx-auto max-w-6xl">
              {(() => {
                const rows = [];
                for (let i = 0; i < OWNER_BENEFITS.length; i += 3) {
                  rows.push(OWNER_BENEFITS.slice(i, i + 3));
                }

                return (
                  <div className="space-y-8">
                    {rows.map((row, rowIdx) => (
                      <div
                        key={rowIdx}
                        className="flex flex-col gap-8 xl:flex-row xl:justify-between"
                      >
                        {row.map((card) => (
                          <div
                            key={card.key}
                            className="flex h-full w-full max-w-sm flex-col xl:w-[30%]"
                          >
                            {card.image ? (
                              <img
                                src={card.image}
                                alt={card.title}
                                className="block h-auto w-full rounded-3xl"
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <div className="flex h-32 w-full items-center justify-center rounded-3xl bg-slate-50 text-3xl text-slate-400">
                                🚗
                              </div>
                            )}

                            <div className="pt-3">
                              <h3 className="text-sm md:text-base font-semibold text-slate-900">
                                {card.title}
                              </h3>
                              <p className="mt-2 text-xs md:text-sm leading-relaxed text-slate-600">
                                {card.sub}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </SectionShell>
        </div>
      </main>

      <Footer />
    </div>
  );
}
